
import { useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { useForecast } from '../../../../contexts/ForecastContext';
import {
    formatDecimal,
    formatPercent,
    formatFrequency,
} from '../../../../utils/formatForecastNumber';
import DownloadForecastToCsv from '../../WizardReview/Blocks/DownloadForecastToCsv';
import DateRangerPickerWithCustomTimezone from '../../../DateRangerPickerWithCustomTimezone/DateRangerPickerWithCustomTimezone';
import ForecastDistributionChart from './ForecastDistributionChart';
import {
    aggregateForecastSchedules,
    filterSchedulesByRange,
    resolveForecastRange,
} from './forecastDistribution';

const ForecastDetails = ({
    selectedScreensLocal = [],
    tierList = {},
    currencySymbol = '',
    campaign,
    forecastPeriod,
    calculatingForecast = false,
}) => {
    const { t } = useTranslation();
    const { forecast: metrics = {} } = useForecast();
    const schedules = useMemo(
        () => (Array.isArray(metrics.schedules) ? metrics.schedules : []),
        [metrics.schedules]
    );
    const hideColumn = false;
    const [selectedRange, setSelectedRange] = useState(null);
    const [tableVisible, setTableVisible] = useState(false);

    const availableRange = useMemo(
        () => resolveForecastRange(forecastPeriod, schedules),
        [forecastPeriod, schedules]
    );
    const availableStart = availableRange?.[0]?.getTime();
    const availableEnd = availableRange?.[1]?.getTime();

    useEffect(() => {
        if (availableStart !== undefined && availableEnd !== undefined) {
            setSelectedRange([new Date(availableStart), new Date(availableEnd)]);
        }
    }, [availableStart, availableEnd]);

    const filteredSchedules = useMemo(
        () =>
            tableVisible
                ? filterSchedulesByRange(schedules, selectedRange)
                : [],
        [schedules, selectedRange, tableVisible]
    );
    const chartPoints = useMemo(
        () => aggregateForecastSchedules(schedules, selectedRange),
        [schedules, selectedRange]
    );

    const createRowKey = (row, index) => {
        const parts = [row.poiId, row.date, row.hour]
            .filter((p) => p !== undefined && p !== null && p !== '')
            .map(String);

        return parts.length ? parts.join('-') : `schedule-${index}`;
    };

    const schedWithKeys = useMemo(() => {
        if (!Array.isArray(filteredSchedules)) return [];

        return filteredSchedules.map((row, index) => ({
            ...row,
            rowKey: row.rowKey ?? createRowKey(row, index),
        }));
    }, [filteredSchedules]);

    const columns = useMemo(
        () => [
            {
                name: 'ID',
                selector: (row) => row.poiId,
                sortable: true,
                omit: hideColumn,
                maxWidth: '8%',
                textAlign: 'left',
            },
            {
                name: 'External ID',
                selector: (row) => row.ScreenExternalId,
                sortable: true,
                omit: hideColumn,
                maxWidth: '8%',
                textAlign: 'left',
            },
            {
                name: t(
                    'sections.management.section.pricing.tierList.columns.screenType'
                ),
                selector: (row) => {
                    if (row.screenType) {
                        return <div className="truncate">{row.screenType}</div>;
                    }
                    const screenForRow = selectedScreensLocal.find(
                        (screen) => screen.id === row.poiId
                    );
                    const tierId = screenForRow?.tierId;
                    return (
                        <div className="truncate">
                            {tierId && tierList[tierId] ? tierList[tierId] : ''}
                        </div>
                    );
                },
                sortable: true,
                omit: hideColumn,
                minWidth: '142px',
            },
            {
                name: t(
                    'sections.campaigns.new-campaign.wizard-audiences.forecast.data-table.date'
                ),
                selector: (row) => row.date,
                sortable: true,
                omit: hideColumn,
                minWidth: '180px',
                textAlign: 'left',
            },
            {
                name: t(
                    'sections.campaigns.new-campaign.wizard-audiences.forecast.data-table.impressions'
                ),
                selector: (row) => row.impressions,
                cell: (row) =>
                    row.impressions !== undefined
                        ? formatDecimal(row.impressions)
                        : 0,
                sortable: true,
                omit: hideColumn,
                minWidth: '122px',
                right: true,
            },
            {
                name: t(
                    'sections.campaigns.new-campaign.wizard-audiences.forecast.data-table.impacts'
                ),
                selector: (row) =>
                    'totalImpacts' in row ? row.totalImpacts : 0,
                cell: (row) =>
                    'totalImpacts' in row
                        ? formatDecimal(row.totalImpacts)
                        : 0,
                sortable: true,
                textAlign: 'left',
                minWidth: '122px',
                omit: hideColumn,
                right: true,
            },
            {
                name: t(
                    'sections.campaigns.new-campaign.wizard-audiences.forecast.data-table.targeted-universe'
                ),
                selector: (row) =>
                    'targetUniverse' in row
                        ? formatDecimal(row.targetUniverse)
                        : 0,
                sortable: true,
                minWidth: '122px',
                omit: hideColumn,
                right: true,
            },
            {
                name: t(
                    'sections.campaigns.new-campaign.wizard-audiences.forecast.data-table.coverage-abs'
                ),
                selector: (row) =>
                    'totalCoverageAbs' in row ? row.totalCoverageAbs : 0,
                cell: (row) =>
                    'totalCoverageAbs' in row
                        ? formatDecimal(row.totalCoverageAbs)
                        : 0,
                sortable: true,
                minWidth: '122px',
                omit: hideColumn,
                right: true,
            },
            {
                name: t(
                    'sections.campaigns.new-campaign.wizard-audiences.forecast.data-table.coverage-rel'
                ),
                selector: (row) =>
                    'totalCoverageRel' in row ? row.totalCoverageRel : null,
                cell: (row) => {
                    if (
                        row.totalCoverageRel === undefined ||
                        row.totalCoverageRel === null
                    ) {
                        return '-';
                    }
                    return formatPercent(row.totalCoverageRel, undefined, {
                        min: 0,
                        max: 1,
                    });
                },
                sortable: true,
                minWidth: '122px',
                omit: hideColumn,
                right: true,
            },
            {
                name: t(
                    'sections.campaigns.new-campaign.wizard-audiences.forecast.data-table.costs'
                ),
                selector: (row) => row.costs,
                cell: (row) =>
                    `${currencySymbol}${formatDecimal(row.costs)}`,
                sortable: true,
                right: true,
                maxWidth: '8%',
                omit: hideColumn,
            },
            {
                name: t(
                    'sections.campaigns.new-campaign.wizard-audiences.forecast.data-table.cpm'
                ),
                selector: (row) =>
                    row.cpm
                        ? `${currencySymbol}${formatDecimal(row.cpm)}`
                        : `${currencySymbol}0`,
                sortable: true,
                right: true,
                maxWidth: '8%',
                omit: hideColumn,
            },
            {
                name: t(
                    'sections.campaigns.new-campaign.wizard-audiences.forecast.data-table.cpi'
                ),
                selector: (row) =>
                    row.cpi
                        ? `${currencySymbol}${formatDecimal(row.cpi)}`
                        : `${currencySymbol}0`,
                sortable: true,
                right: true,
                maxWidth: '10px ',
                omit: hideColumn,
            },
            {
                name: t(
                    'sections.campaigns.new-campaign.wizard-audiences.forecast.data-table.frecuency'
                ),
                selector: (row) =>
                    row.totalFrequency
                        ? formatFrequency(row.totalFrequency)
                        : 0,
                sortable: true,
                right: true,
                minWidth: '122px',
                omit: hideColumn,
            },
            {
                name: t(
                    'sections.campaigns.new-campaign.wizard-audiences.forecast.data-table.grp'
                ),
                selector: (row) =>
                    'grp' in row ? formatDecimal(row.grp) : 0,
                sortable: true,
                omit: hideColumn,
            },
            {
                name: 'cps',
                selector: (row) =>
                    'cps' in row ? formatDecimal(row.cps) : 0,
                sortable: true,
                omit: hideColumn,
            },
            {
                name: 'spm',
                selector: (row) =>
                    'spm' in row ? formatDecimal(row.spm) : 0,
                sortable: true,
                omit: hideColumn,
            },
        ],
        [
            hideColumn,
            tierList,
            selectedScreensLocal,
            metrics.screensUsed,
            currencySymbol,
            t,
        ]
    );

    return (
        <div className="body rounded-sm mt-14">
            <div className="mb-3">
                {availableRange && selectedRange ? (
                    <label className="text-xs text-gray-600">
                        <span className="mb-1 block">
                            {t(
                                'sections.campaigns.new-campaign.wizard-audiences.forecast.select-date-range'
                            )}
                        </span>
                        <DateRangerPickerWithCustomTimezone
                            className="campaignPicker"
                            clearIcon={null}
                            minDate={availableRange[0]}
                            maxDate={availableRange[1]}
                            value={selectedRange}
                            onChange={(range) => {
                                if (
                                    Array.isArray(range) &&
                                    range[0] &&
                                    range[1]
                                ) {
                                    setSelectedRange(range);
                                }
                            }}
                        />
                    </label>
                ) : (
                    <div />
                )}
            </div>
            <ForecastDistributionChart
                points={chartPoints}
                isLoading={calculatingForecast}
                currencySymbol={currencySymbol}
            />
            {schedules.length ? (
                <div className="mt-3 flex w-full flex-wrap items-center justify-between gap-3">
                    <button
                        type="button"
                        aria-expanded={tableVisible}
                        aria-controls="forecast-details-table"
                        onClick={() =>
                            setTableVisible((visible) => !visible)
                        }
                        className="rounded border border-blue-400 px-3 py-2 text-sm font-medium text-blue-500 hover:text-blue-700 focus:outline-none">
                        {t(
                            `sections.campaigns.new-campaign.wizard-audiences.forecast.${
                                tableVisible ? 'hide-table' : 'show-table'
                            }`
                        )}
                    </button>
                    <div className="ml-auto">
                        <DownloadForecastToCsv
                            campaign={campaign}
                            selectedScreensLocal={selectedScreensLocal}
                            forecastData={schedules}
                            tierList={tierList}
                        />
                    </div>
                </div>
            ) : null}
            {tableVisible && (
                <div id="forecast-details-table" className="mt-4">
                    <DataTable
                        columns={columns}
                        fixedHeaderScrollHeight={'70vh'}
                        subHeader={false}
                        pagination
                        paginationRowsPerPageOptions={[
                            10,
                            30,
                            60,
                            120,
                            300,
                        ]}
                        paginationPerPage={60}
                        selectableRowsHighlight={true}
                        data={schedWithKeys}
                        noDataComponent={t(
                            'common.components.react-data-table.no-data'
                        )}
                        keyField={'rowKey'}
                        progressComponent={
                            <>
                                {t(
                                    'sections.campaigns.new-campaign.wizard-screens.screens.loading-screens'
                                )}
                            </>
                        }
                    />
                </div>
            )}
        </div>
    );
};

const TableComp = ({ canViewForecastDetails, ...props }) =>
    canViewForecastDetails ? <ForecastDetails {...props} /> : null;

export default TableComp;
